import {ProvidingListApplication} from '../../../shared/models/api';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {CardApplicationReason} from '../../../types/enums/cardApplicationReason';
import {CardApplicationStatus} from '../../../types/enums/cardApplicationStatus';
import {CardStatus} from '../../../types/enums/cardStatus';

export type ActivationDecisionInput = {
  cardStatusId?: number | null;
  uscIsBlocked?: boolean;
  applications?: ProvidingListApplication[] | null;
};

export type ActivationDecision = {
  shouldActivate: boolean;
  pendingApplication?: ProvidingListApplication;
};

const isOwnApplication = (application: ProvidingListApplication) => !application.isChildApplication;

const compareApplicationsByNewest = (a: ProvidingListApplication, b: ProvidingListApplication) => {
  const byDate = (b.dateTimeFiling ?? '').localeCompare(a.dateTimeFiling ?? '');
  if (byDate !== 0) {
    return byDate;
  }
  return (b.id ?? 0) - (a.id ?? 0);
};

/** Block creates Approved + UnsuitabilityForUse (reason 6), not a dedicated "block" reason. */
export const isCardBlockApplication = (application: ProvidingListApplication | undefined) =>
  application?.typeApplicationId === CardApplicationReason.UnsuitabilityForUse &&
  application?.statusId === CardApplicationStatus.Accepted;

/** Unfinished issuance/reissue cycle — exclude Approved (Accepted). */
export const isPendingCardIssueApplication = (application: ProvidingListApplication) =>
  isOwnApplication(application) &&
  (application.statusId === CardApplicationStatus.AcceptFromClient ||
    application.statusId === CardApplicationStatus.OnReview) &&
  Boolean(application.cardDeliveryInfo);

export const findLatestOwnApplication = (
  applications: ProvidingListApplication[] | null | undefined,
): ProvidingListApplication | undefined => {
  if (!Array.isArray(applications)) {
    return undefined;
  }

  return applications.filter(isOwnApplication).sort(compareApplicationsByNewest)[0];
};

export const findLatestCardIssueApplication = (
  applications: ProvidingListApplication[] | null | undefined,
): ProvidingListApplication | undefined => {
  if (!Array.isArray(applications)) {
    return undefined;
  }

  return applications.filter(isPendingCardIssueApplication).sort(compareApplicationsByNewest)[0];
};

/**
 * Latest own application with delivery — including Approved.
 * Needed for trackingNumber when card is already InDelivery/Arrived.
 */
export const findLatestOwnApplicationWithDelivery = (
  applications: ProvidingListApplication[] | null | undefined,
): ProvidingListApplication | undefined => {
  if (!Array.isArray(applications)) {
    return undefined;
  }

  return applications
    .filter((application) => isOwnApplication(application) && Boolean(application.cardDeliveryInfo))
    .sort(compareApplicationsByNewest)[0];
};

/**
 * Single source of truth for password and ESIA login:
 * activate card vs enter cabinet.
 * `cardStatusId == null` alone does not open activation.
 */
export const getCardActivationDecision = (input: ActivationDecisionInput): ActivationDecision => {
  const pendingApplication = findLatestCardIssueApplication(input.applications);
  const latestOwnApplication = findLatestOwnApplication(input.applications);
  const latestIsCardBlock = isCardBlockApplication(latestOwnApplication);

  const isCardActivationRequired =
    input.cardStatusId === CardStatus.New ||
    input.cardStatusId === CardStatus.InDelivery ||
    input.cardStatusId === CardStatus.Arrived ||
    input.cardStatusId === CardStatus.Reserved;

  const cardIsActive = input.cardStatusId === CardStatus.Active && !input.uscIsBlocked;

  const shouldActivate =
    isCardActivationRequired || (Boolean(pendingApplication) && !cardIsActive && !latestIsCardBlock);

  return {
    shouldActivate,
    pendingApplication: shouldActivate ? pendingApplication : undefined,
  };
};

export const shouldActivateCardFromEsia = (
  regInfo: Pick<AuthorizeEsiaResponsePayload, 'cardStatusId' | 'cardApplicationStatusId' | 'cardDeliveryInfo'> | null,
): boolean => {
  if (!regInfo) return false;
  // ESIA can omit delivery for AcceptFromClient; other snapshots follow the shared activation decision.
  if (regInfo.cardApplicationStatusId === CardApplicationStatus.AcceptFromClient) return true;

  return getCardActivationDecision({
    cardStatusId: regInfo.cardStatusId,
    applications: [
      {
        statusId: regInfo.cardApplicationStatusId,
        cardDeliveryInfo: regInfo.cardDeliveryInfo as ProvidingListApplication['cardDeliveryInfo'],
      },
    ],
  }).shouldActivate;
};
